import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { suppliersService } from '../services/suppliers.service';
import type { SupplierCreatePayload, SupplierUpdatePayload } from '../types/suppliers.types';
import { toast } from 'sonner';

export function useSuppliers() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const userId = profile?.id || '';
  const queryClient = useQueryClient();

  const queryKey = ['inventory', 'suppliers'];

  const query = useQuery({
    queryKey,
    queryFn: () => suppliersService.fetchSuppliers(),
  });

  const createMutation = useMutation({
    mutationFn: (payload: SupplierCreatePayload) => suppliersService.createSupplier(userId, companyId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Thêm nhà cung cấp thành công');
    },
    onError: (err: any) => {
      console.error(err);
      toast.error(err.message || 'Lỗi khi thêm nhà cung cấp');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SupplierUpdatePayload }) => 
      suppliersService.updateSupplier(userId, companyId, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Cập nhật nhà cung cấp thành công');
    },
    onError: (err: any) => {
      console.error(err);
      toast.error(err.message || 'Lỗi khi cập nhật nhà cung cấp');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => suppliersService.deleteSupplier(userId, companyId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Xóa nhà cung cấp thành công');
    },
    onError: (err: any) => {
      console.error(err);
      toast.error(err.message || 'Lỗi khi xóa nhà cung cấp');
    },
  });

  return {
    data: query.data || [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    createSupplier: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateSupplier: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteSupplier: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
